"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHotelRoomAvailability = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
const dayjs = require("dayjs");
const slugify_1 = require("../slugify");
const getHotelRoomAvailability = (dateRange, contracts) => {
    const dayOfWeekMap = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const isInDateRangeAndAvailableOnDay = (startDate, endDate, checkDate, availabilityDays) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const check = new Date(checkDate);
        const dayOfWeek = dayOfWeekMap[check.getDay()];
        return check >= start && check <= end && availabilityDays[dayOfWeek];
    };
    const getPriceBandForDate = (priceBands, date) => {
        const band = priceBands.find((band) => {
            const start = new Date(band.from_date);
            const end = new Date(band.to_date);
            const check = new Date(date);
            return check >= start && check <= end;
        });
        return band;
    };
    const availabilityBySupplier = {};
    contracts.forEach((contract) => {
        const { supplier_id, supplier, products, price_bands } = contract;
        if (!availabilityBySupplier[supplier_id]) {
            availabilityBySupplier[supplier_id] = {
                id: supplier_id,
                name: supplier.name,
                currency: supplier.currency,
                rooms: {},
            };
        }
        if (!products)
            return console.error("No products found in contract", contract);
        products.forEach((product) => {
            const { hotel_room, board_options, occupancy, availability_days } = product;
            const roomName = hotel_room?.name.en;
            const room = hotel_room;
            const roomId = hotel_room?.id || "";
            const maxOccupancy = hotel_room?.max_occupancy;
            const possibleOccupancies = occupancy === 0
                ? [...Array(maxOccupancy).keys()].map((x) => x + 1)
                : [occupancy];
            board_options.forEach((option) => {
                if (!availabilityBySupplier[supplier_id].rooms[roomId]) {
                    availabilityBySupplier[supplier_id].rooms[roomId] = {
                        room_type: roomName,
                        room,
                        maxOccupancy,
                        boardOptions: {},
                    };
                }
                possibleOccupancies.forEach((occupancyOption) => {
                    if (!availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type]) {
                        availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type] = {};
                    }
                    if (!availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][occupancyOption]) {
                        availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][occupancyOption] = {
                            dates: {},
                        };
                    }
                    dateRange.forEach((date) => {
                        if (isInDateRangeAndAvailableOnDay(contract.start_date, contract.end_date, date, availability_days)) {
                            if (!price_bands)
                                return console.error("No price bands found in contract", contract);
                            const priceBand = getPriceBandForDate(price_bands, date);
                            const extraBedCost = option.extra_bed_cost;
                            const extraBedAvailable = option.extra_bed_available;
                            const surchargePerPerson = option.surcharge_per_person; // This might need to be dynamically retrieved based on the date and price band
                            availabilityBySupplier[supplier_id].rooms[roomId].boardOptions[option.type][occupancyOption].dates[date] = {
                                surchargePerPerson,
                                extraBedCost,
                                extraBedAvailable,
                                priceBandId: priceBand ? priceBand.id : null, // Store the applicable price band ID
                            };
                        }
                    });
                });
            });
        });
    });
    // When converting to the desired structure, include the surchargePerPerson for each date
    const resultArray = Object.values(availabilityBySupplier).map((supplier) => {
        const availability = Object.entries(supplier.rooms)
            .map(([roomId, { room, maxOccupancy, boardOptions }]) => {
            const boardOptionsArray = Object.entries(boardOptions).flatMap(([optionType, occupancies]) => {
                return Object.entries(occupancies)
                    .map(([occupancy, { dates }]) => ({
                    type: optionType,
                    rate_ref: `rate_${(0, slugify_1.slugify)(room.name.en)}_${(0, slugify_1.slugify)(supplier.name)}_${(0, slugify_1.slugify)(optionType)}_${parseInt(occupancy)}_pax`,
                    occupancy: parseInt(occupancy),
                    dates: Object.entries(dates).map(([date, { surchargePerPerson, extraBedCost, extraBedAvailable, priceBandId, },]) => {
                        const rates = contracts
                            .filter((contract) => contract.supplier_id === supplier.id &&
                            contract.start_date <= date &&
                            contract.end_date >= date)
                            .flatMap((contract) => {
                            if (!contract.products)
                                return [];
                            return contract.products
                                .filter((product) => {
                                const key = dayOfWeekMap[new Date(date).getDay()];
                                const filter_occupancy = product.occupancy === 0
                                    ? true
                                    : product.occupancy === parseInt(occupancy);
                                return (product?.hotel_room?.id === roomId &&
                                    product.availability_days[key] &&
                                    filter_occupancy);
                            })
                                .flatMap((product) => {
                                if (!product.rates)
                                    return [];
                                return product.rates.filter((rate) => {
                                    if (rate.price_band_id !== priceBandId)
                                        return false;
                                    // if the rate.min_nights is greater than the number of nights, skip it
                                    if (rate.min_nights > dateRange.length)
                                        return false;
                                    return true;
                                });
                            });
                        })
                            .sort((a, b) => {
                            // sort by cheapest rate
                            return a.sell_price - b.sell_price;
                        });
                        return {
                            date,
                            board_surcharge: surchargePerPerson,
                            extra_bed_cost: extraBedCost,
                            extra_bed_available: extraBedAvailable,
                            ...(rates.length > 0 ? rates[0] : null),
                        };
                    }),
                }))
                    .filter((boardOption) => boardOption.dates.length === dateRange.length); // Ensure full availability
            });
            return {
                hotel_room_id: roomId,
                room_ref: `rate_${(0, slugify_1.slugify)(room.name.en)}_${(0, slugify_1.slugify)(supplier.name)}`,
                room,
                max_occupancy: maxOccupancy,
                board_options: boardOptionsArray,
            };
        })
            .filter((room) => room.board_options.length > 0)
            .filter((room) => {
            return room.board_options.every((option) => {
                return option.dates.every((date) => {
                    if (date.book_before &&
                        dayjs(date.book_before).toDate() < new Date()) {
                        return false;
                    }
                    return date;
                });
            });
        })
            .sort((a, b) => {
            // sorty by cheapest rate
            const aRate = a.board_options[0].dates.reduce((prev, current) => {
                if (prev.sell_price && current.sell_price)
                    return prev.sell_price < current.sell_price ? prev : current;
                return prev;
            }).sell_price || 0;
            const bRate = b.board_options[0].dates.reduce((prev, current) => {
                if (prev.sell_price && current.sell_price)
                    return prev.sell_price < current.sell_price ? prev : current;
                return prev;
            }).sell_price || 0;
            return aRate - bRate;
        });
        return {
            id: supplier.id,
            name: supplier.name,
            currency: supplier.currency,
            dates: dateRange,
            availability,
        };
    });
    return resultArray;
};
exports.getHotelRoomAvailability = getHotelRoomAvailability;
